/**
 * OptOutKeywordForm — create/edit dialog body for `/admin/opt-out-keywords`.
 *
 * Client-side validation:
 *   - keyword is required (1-100 chars).
 *   - when `matchType === 'regex'`, we compile the keyword via `new RegExp()`
 *     in a try/catch. A compile failure surfaces inline and blocks submit,
 *     mirroring the server's 400 `VALIDATION_FAILED` behaviour so operators
 *     don't burn a round-trip to learn their pattern was broken.
 */

import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export type KeywordMatchType = 'exact' | 'contains' | 'regex';

export interface OptOutKeywordFormValue {
  keyword: string;
  matchType: KeywordMatchType;
  active: boolean;
}

export const DEFAULT_KEYWORD_FORM: OptOutKeywordFormValue = {
  keyword: '',
  matchType: 'contains',
  active: true,
};

interface Props {
  mode: 'create' | 'edit';
  initial?: OptOutKeywordFormValue;
  submitting?: boolean;
  onSubmit: (next: OptOutKeywordFormValue) => void;
  onCancel: () => void;
}

const MATCH_TYPE_OPTIONS: KeywordMatchType[] = ['exact', 'contains', 'regex'];

function validateRegex(pattern: string): string | null {
  try {
    new RegExp(pattern, 'i');
    return null;
  } catch (err) {
    return err instanceof Error ? err.message : 'regex inválida';
  }
}

export function OptOutKeywordForm({ mode, initial, submitting, onSubmit, onCancel }: Props) {
  const [form, setForm] = useState<OptOutKeywordFormValue>(initial ?? DEFAULT_KEYWORD_FORM);
  const [keywordError, setKeywordError] = useState<string | null>(null);

  function patch<K extends keyof OptOutKeywordFormValue>(key: K, value: OptOutKeywordFormValue[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleKeywordChange(next: string) {
    patch('keyword', next);
    if (next === '') {
      setKeywordError(null);
      return;
    }
    if (form.matchType === 'regex') {
      setKeywordError(validateRegex(next));
    } else {
      setKeywordError(null);
    }
  }

  function handleMatchTypeChange(next: KeywordMatchType) {
    patch('matchType', next);
    if (next === 'regex' && form.keyword.length > 0) {
      setKeywordError(validateRegex(form.keyword));
    } else {
      setKeywordError(null);
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (form.keyword.trim().length === 0) {
      setKeywordError('Keyword obrigatória.');
      return;
    }
    if (form.matchType === 'regex') {
      const err = validateRegex(form.keyword);
      if (err) {
        setKeywordError(err);
        return;
      }
    }
    onSubmit(form);
  }

  const disabled = !!keywordError || form.keyword.trim().length === 0 || !!submitting;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="keyword-input">Keyword</Label>
        <Input
          id="keyword-input"
          aria-label="Keyword"
          value={form.keyword}
          onChange={(e) => handleKeywordChange(e.target.value)}
          placeholder="pare / sair / ^stop.*$"
          maxLength={100}
        />
        {keywordError ? (
          <p className="text-sm text-destructive" role="alert">
            {keywordError}
          </p>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor="match-type-select">Tipo de match</Label>
        <select
          id="match-type-select"
          aria-label="Tipo de match"
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={form.matchType}
          onChange={(e) => handleMatchTypeChange(e.target.value as KeywordMatchType)}
        >
          {MATCH_TYPE_OPTIONS.map((mt) => (
            <option key={mt} value={mt}>
              {mt}
            </option>
          ))}
        </select>
        <p className="text-xs text-muted-foreground">
          <code>exact</code>: match integral normalizado. <code>contains</code>: substring.{' '}
          <code>regex</code>: padrão regex (case-insensitive).
        </p>
      </div>

      <div className="flex items-center gap-2">
        <input
          id="active-checkbox"
          aria-label="Ativa"
          type="checkbox"
          checked={form.active}
          onChange={(e) => patch('active', e.target.checked)}
          className="h-4 w-4 rounded border-input"
        />
        <Label htmlFor="active-checkbox">Ativa</Label>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={!!submitting}>
          Cancelar
        </Button>
        <Button type="submit" disabled={disabled}>
          {mode === 'create' ? 'Criar keyword' : 'Salvar alterações'}
        </Button>
      </div>
    </form>
  );
}
